import { Suspense, lazy } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import {
  AuthProvider,
  RequireAuth,
  GuestGuard,
  AppReadyProvider,
} from "@/components/auth";
import { PermissionsProvider } from "@/hooks/PermissionsProvider";
import { PERMISSION_KEYS } from "@/hooks/permissionKeys";
import {
  PermissionGuard,
  SuperAdminGuard,
} from "@/components/common/PermissionGuard";
import { ViewAsBanner } from "@/components/common/ViewAsBanner";
import { QueryProvider } from "@/components/providers/QueryProvider";
import { I18nProvider } from "@/components/providers/I18nProvider";
// Imported from its own file, not the "@/components/providers" barrel: the
// barrel also re-exports the admin search and service-creator providers, and
// importing through it from here put them, and the admin services they pull
// in, into the entry chunk every public visitor downloads.
import { ThemeProvider } from "@/components/providers/ThemeProvider";
import { TooltipProvider } from "@/components/ui/tooltip";
import { pickRouteLoadingSkeleton } from "@/components/skeletons";
import { InitialLoaderHider } from "@/components/common/InitialLoaderHider";
import { CAN_RUN_STAGING_TESTS } from "@/utils/stagingIntegrationTests";

/*
 * Route chunks load with plain `lazy`. A previous `lazyWithMinDelay` helper
 * held every admin chunk for a fixed 2s so its skeleton was "perceivable" —
 * but the page's data fetch cannot start until the chunk resolves, so that
 * delay was added BEFORE the first query rather than overlapped with it. It
 * cost a flat 2s on Dashboard, Services, Users, Roles and Reports, on every
 * visit. The route skeletons still render while the chunk is in flight; they
 * just no longer wait for a timer that guarantees nothing.
 */

// Layouts
import PublicLayout from "@/components/layouts/PublicLayout";

// The application detail page's early download: see its route below.
import {
  isApplicationDetailPath,
  loadApplicationDetailPage,
  preloadApplicationDetailPage,
} from "@/pages/admin/applicationDetailPreload";
// A public page's early download and first read: see the /track rewrite below.
import {
  publicPages,
  startPublicRoute,
} from "@/pages/public/publicRoutePreload";

/*
 * The admin shell — sidebar, header, the admin search and every service they
 * reach — is its own chunk, so the entry chunk every public visitor downloads
 * no longer carries it. On an admin URL the chunk is requested as this module
 * evaluates, alongside the session check, rather than after it: AuthGuard only
 * renders the layout once the session is known, and a lazy component starts
 * loading when it first renders. The top-level Suspense fallback shows the
 * admin shell skeleton meanwhile.
 */
const loadAdminLayout = () => import("@/components/layouts/AdminLayout");
const AdminLayout = lazy(loadAdminLayout);
if (
  typeof window !== "undefined" &&
  window.location.pathname.startsWith("/admin")
) {
  // The catch only keeps an early failure from being reported twice: the lazy
  // component makes the same request when it renders, and a failure there
  // reaches the route's error handling exactly as it always has.
  loadAdminLayout().catch(() => undefined);
}

/*
 * The public pages load through the same imports that App starts early on a
 * hard load of one of them (startPublicRoute, below the /track rewrite), so the
 * route finds its page already on the way. See publicRoutePreload.ts.
 */
const HomePage = lazy(publicPages.HomePage);
const ServiceCataloguePage = lazy(publicPages.ServiceCataloguePage);
const ServiceDetailPage = lazy(publicPages.ServiceDetailPage);
const ApplicationFormPage = lazy(publicPages.ApplicationFormPage);
const TrackApplicationPage = lazy(publicPages.TrackApplicationPage);
const ContinueApplicationPage = lazy(publicPages.ContinueApplicationPage);
const UnlistedServicePage = lazy(publicPages.UnlistedServicePage);
const UnlistedApplicationFormPage = lazy(
  publicPages.UnlistedApplicationFormPage,
);
const HelpPage = lazy(publicPages.HelpPage);
const GuidePage = lazy(publicPages.GuidePage);
const AboutPage = lazy(publicPages.AboutPage);
const PrivacyPage = lazy(publicPages.PrivacyPage);
const TermsPage = lazy(publicPages.TermsPage);
const CapeDirectoryPage = lazy(publicPages.CapeDirectoryPage);
const CapeApplicationFormPage = lazy(publicPages.CapeApplicationFormPage);
const AccessibilityPage = lazy(publicPages.AccessibilityPage);
const ExternalReviewerPage = lazy(publicPages.ExternalReviewerPage);

const DashboardPage = lazy(
  () => import("@/pages/admin/DashboardPage"),
);
const ApplicationsHubPage = lazy(
  () => import("@/pages/admin/ApplicationsHubPage"),
);
const ApplicationsListPage = lazy(
  () => import("@/pages/admin/ApplicationsListPage"),
);
/*
 * An application's page is where a notification e-mail lands, so it is often
 * the first thing a hard load renders. Its chunk is requested now, with the
 * admin shell's, rather than once the session check has finished and the
 * route renders — uncached, after a deploy, that download used to sit in
 * front of every request the page makes. See applicationDetailPreload.ts.
 */
const ApplicationDetailPage = lazy(loadApplicationDetailPage);
if (
  typeof window !== "undefined" &&
  isApplicationDetailPath(window.location.pathname)
) {
  preloadApplicationDetailPage();
}
const ServicesHubPage = lazy(
  () => import("@/pages/admin/ServicesHubPage"),
);
const ServiceCreatorHub = lazy(
  () => import("@/pages/admin/ServiceCreatorHub"),
);
const ServiceCreatorPage = lazy(
  () => import("@/pages/admin/ServiceCreatorPage"),
);
const ServicesBrowserPage = lazy(
  () => import("@/pages/admin/ServicesBrowserPage"),
);
const ServiceFormPreviewPage = lazy(
  () => import("@/pages/admin/ServiceFormPreviewPage"),
);
const AllApplicationsListPage = lazy(
  () => import("@/pages/admin/AllApplicationsListPage"),
);
const UsersManagementPage = lazy(
  () => import("@/pages/admin/UsersManagement"),
);
const ViewAsPage = lazy(() => import("@/pages/admin/ViewAsPage"));
const RolesManagementPage = lazy(
  () => import("@/pages/admin/RolesManagement"),
);
const ReportsPage = lazy(() => import("@/pages/admin/ReportsPage"));
const SupportMessagesPage = lazy(
  () => import("@/pages/admin/SupportMessagesPage"),
);
const AdminSearchPage = lazy(() => import("@/pages/admin/AdminSearchPage"));
const TemplateEditorPage = lazy(
  () => import("@/pages/admin/TemplateEditorPage"),
);
const FAQsManagementPage = lazy(
  () => import("@/pages/admin/FAQsManagementPage"),
);
const AnnouncementsManagementPage = lazy(
  () => import("@/pages/admin/AnnouncementsManagementPage"),
);
const ProfilePage = lazy(() => import("@/pages/admin/ProfilePage"));
const CapeApplicationsPage = lazy(
  () => import("@/pages/admin/CapeApplicationsPage"),
);
const CapeApplicationDetailPage = lazy(
  () => import("@/pages/admin/CapeApplicationDetailPage"),
);

/*
 * Two pages for testing, not for use. Each is declared behind a condition the
 * build turns into a constant, and both constants are false in the deployed
 * build, so there the branch is dead: Rollup drops the `import()`, the page's
 * chunk is never emitted, and its route below is never registered. A plain
 * top-level `lazy()` would emit the chunk anyway, and an unconditional route
 * is open to anyone who types the URL.
 */

// Deletes every service and everything hanging off it. Development only. DEV
// says nothing about which project the dev server talks to — that is whatever
// .env.local names, and it can be production — so even there it takes a
// signed-in Super Admin.
const CleanupPage = import.meta.env.DEV
  ? lazy(() => import("@/pages/admin/CleanupPage"))
  : null;

// What `npm run test:integration:staging` opens. That runner serves a
// production build (`vite build` + `vite preview`), so this keys off the
// runner's flags, not DEV: the route exists exactly when the page may run.
const IntegrationTestPage = CAN_RUN_STAGING_TESTS
  ? lazy(() => import("@/pages/admin/IntegrationTestPage"))
  : null;

const LoginPage = lazy(() => import("@/pages/auth/LoginPage"));

const AuthCallbackPage = lazy(() => import("@/pages/auth/AuthCallbackPage"));

const MicrosoftOnboardingPage = lazy(
  () => import("@/pages/auth/MicrosoftOnboardingPage"),
);

const NotFoundPage = lazy(() => import("@/pages/errors/NotFoundPage"));
const UnauthorizedPage = lazy(() => import("@/pages/errors/UnauthorizedPage"));

function LegacyTrackRedirect() {
  const location = useLocation();
  return <Navigate to={`/track-application${location.search}`} replace />;
}

/*
 * A hard load of /track comes from a link sent before the tracking page moved
 * to /track-application, and from the e-mails that still build /track?...
 * (send-email, staff-action, submit-application, notificationService). The URL
 * is put back here, before the router reads it, so the tracking page renders
 * straight away like any other page. Through LegacyTrackRedirect, the layout
 * committed around an empty <main> first: the splash went, and the visitor
 * watched a blank page while the tracking chunk downloaded. The route stays
 * for an in-app link to /track.
 */
if (
  typeof window !== "undefined" &&
  /^\/track\/?$/.test(window.location.pathname)
) {
  window.history.replaceState(
    window.history.state,
    "",
    `/track-application${window.location.search}${window.location.hash}`,
  );
}

/*
 * On a hard load of a public page, its chunk and its first read start now,
 * while the entry finishes, i18n starts and the session is checked, instead of
 * once the route renders. After the /track rewrite, so it sees the path the
 * router will. Any other path (admin, sign-in, a 404) starts nothing here.
 */
if (typeof window !== "undefined") {
  startPublicRoute(window.location.pathname);
}

function NormalizePathRedirect() {
  const location = useLocation();
  const normalizedPathname = location.pathname.replace(/\/{2,}/g, "/");

  if (normalizedPathname !== location.pathname) {
    return (
      <Navigate
        to={`${normalizedPathname}${location.search}${location.hash}`}
        replace
      />
    );
  }

  return null;
}

function RouteLoadingFallback() {
  const location = useLocation();
  // Only admin routes show a skeleton. Public / auth / error routes render
  // nothing so the previous page stays visible until the new chunk resolves.
  if (!location.pathname.startsWith("/admin")) {
    return null;
  }
  return <>{pickRouteLoadingSkeleton(location.pathname)}</>;
}

function App() {
  return (
    <ThemeProvider>
      <I18nProvider>
        <QueryProvider>
          <AuthProvider>
            <PermissionsProvider>
              <AppReadyProvider>
                <TooltipProvider>
                  <Router
                    future={{
                      v7_relativeSplatPath: true,
                      v7_startTransition: true,
                    }}
                  >
                    <NormalizePathRedirect />
                    {/*
                      Mounted above <Routes> so it survives every navigation and
                      covers the public layouts too — a Super Admin viewing as a
                      head teacher can walk onto a public page, and the tab must
                      keep saying whose account it is.
                    */}
                    <ViewAsBanner />
                    <Suspense fallback={<RouteLoadingFallback />}>
                      <InitialLoaderHider />
                      <Routes>
                        {/* Public Routes */}
                        <Route path="/" element={<PublicLayout />}>
                          <Route index element={<HomePage />} />
                          <Route
                            path="services"
                            element={<ServiceCataloguePage />}
                          />
                          <Route
                            path="services/:serviceSlug"
                            element={<ServiceDetailPage />}
                          />
                          <Route
                            path="services/:serviceSlug/apply"
                            element={<ApplicationFormPage />}
                          />
                          <Route
                            path="services/:serviceSlug/track"
                            element={<TrackApplicationPage />}
                          />
                          <Route
                            path="track-application"
                            element={<TrackApplicationPage />}
                          />
                          <Route
                            path="continue-application"
                            element={<ContinueApplicationPage />}
                          />
                          <Route
                            path="track"
                            element={<LegacyTrackRedirect />}
                          />
                          <Route
                            path="services/cape-registration"
                            element={<CapeDirectoryPage />}
                          />
                          <Route
                            path="services/cape-registration/apply"
                            element={<CapeApplicationFormPage />}
                          />
                          {/* An unlisted service, reached only through its
                              link. Under /s, apart from every /services
                              route, so a token can never be read as a slug. */}
                          <Route
                            path="s/:token"
                            element={<UnlistedServicePage />}
                          />
                          <Route
                            path="s/:token/apply"
                            element={<UnlistedApplicationFormPage />}
                          />
                          <Route path="help" element={<HelpPage />} />
                          <Route path="guide" element={<GuidePage />} />
                          <Route path="about" element={<AboutPage />} />
                          <Route path="privacy" element={<PrivacyPage />} />
                          <Route path="terms" element={<TermsPage />} />
                          <Route
                            path="external-reviewer"
                            element={<ExternalReviewerPage />}
                          />
                          <Route
                            path="accessibility"
                            element={<AccessibilityPage />}
                          />
                        </Route>

                        {/* Auth Routes - Only accessible to non-authenticated users */}
                        <Route
                          path="/login"
                          element={
                            <GuestGuard>
                              <LoginPage />
                            </GuestGuard>
                          }
                        />
                        <Route path="/auth/login" element={<LoginPage />} />

                        {/* Retired password / self-registration entry points.
                            Microsoft sign-in is the only way in, so these keep
                            old bookmarks and already-sent emails landing on
                            something actionable instead of a 404. */}
                        <Route
                          path="/signup"
                          element={<Navigate to="/login" replace />}
                        />
                        <Route
                          path="/forgot-password"
                          element={<Navigate to="/login" replace />}
                        />
                        <Route
                          path="/reset-password"
                          element={<Navigate to="/login" replace />}
                        />
                        <Route
                          path="/setup-password"
                          element={<Navigate to="/login" replace />}
                        />
                        <Route
                          path="/auth/change-password"
                          element={<Navigate to="/login" replace />}
                        />

                        {/* Microsoft (Azure AD) OAuth return trip.
                            Deliberately NOT behind GuestGuard — a session
                            arrives mid-render and the guard would navigate away
                            before these pages could gate or provision. */}
                        <Route
                          path="/auth/callback"
                          element={<AuthCallbackPage />}
                        />
                        <Route
                          path="/auth/complete-profile"
                          element={<MicrosoftOnboardingPage />}
                        />

                        {/* Unauthorized Page */}
                        <Route
                          path="/unauthorized"
                          element={<UnauthorizedPage />}
                        />

                        {/* Staging integration runner, only in a build made
                            for it (see IntegrationTestPage above). It sits
                            outside the admin shell and does its own sign-in
                            and permission checks. */}
                        {IntegrationTestPage && (
                          <Route
                            path="/admin/integration-test"
                            element={<IntegrationTestPage />}
                          />
                        )}

                        {/* Staff preview of a service's application form, in
                            any status. Outside the admin shell so it shows the
                            form as the public will see it, and outside /admin
                            so the window does not load that shell either; the
                            guards are what keep it staff-only. What a draft's
                            form shows is still decided by RLS (see the page). */}
                        <Route
                          path="/preview/services/:serviceId"
                          element={
                            <RequireAuth>
                              <PermissionGuard
                                permissions={[
                                  PERMISSION_KEYS.SERVICES_VIEW,
                                  PERMISSION_KEYS.SERVICES_CREATE,
                                  PERMISSION_KEYS.SERVICES_EDIT,
                                ]}
                                showAccessDenied
                              >
                                <ServiceFormPreviewPage />
                              </PermissionGuard>
                            </RequireAuth>
                          }
                        />

                        {/* Admin Routes - Protected */}
                        <Route
                          path="/admin"
                          element={
                            <RequireAuth>
                              <AdminLayout />
                            </RequireAuth>
                          }
                        >
                          <Route index element={<DashboardPage />} />
                          <Route path="dashboard" element={<DashboardPage />} />
                          <Route path="search" element={<AdminSearchPage />} />
                          <Route
                            path="applications"
                            element={
                              <PermissionGuard
                                permissions={[
                                  PERMISSION_KEYS.APPLICATIONS_VIEW_OWN,
                                  PERMISSION_KEYS.APPLICATIONS_VIEW_ALL,
                                ]}
                                showAccessDenied
                              >
                                <ApplicationsHubPage />
                              </PermissionGuard>
                            }
                          />
                          <Route
                            path="applications/category/:categoryId"
                            element={
                              <PermissionGuard
                                permissions={[
                                  PERMISSION_KEYS.APPLICATIONS_VIEW_OWN,
                                  PERMISSION_KEYS.APPLICATIONS_VIEW_ALL,
                                ]}
                                showAccessDenied
                              >
                                <ApplicationsListPage />
                              </PermissionGuard>
                            }
                          />
                          <Route
                            path="applications/service/:serviceId"
                            element={
                              <PermissionGuard
                                permissions={[
                                  PERMISSION_KEYS.APPLICATIONS_VIEW_OWN,
                                  PERMISSION_KEYS.APPLICATIONS_VIEW_ALL,
                                ]}
                                showAccessDenied
                              >
                                <ApplicationsListPage />
                              </PermissionGuard>
                            }
                          />
                          <Route
                            path="applications/all"
                            element={
                              <PermissionGuard
                                permissions={[
                                  PERMISSION_KEYS.APPLICATIONS_VIEW_OWN,
                                  PERMISSION_KEYS.APPLICATIONS_VIEW_ALL,
                                ]}
                                showAccessDenied
                              >
                                <AllApplicationsListPage />
                              </PermissionGuard>
                            }
                          />
                          <Route
                            path="applications/:applicationId"
                            element={
                              <PermissionGuard
                                permissions={[
                                  PERMISSION_KEYS.APPLICATIONS_VIEW_OWN,
                                  PERMISSION_KEYS.APPLICATIONS_VIEW_ALL,
                                ]}
                                showAccessDenied
                              >
                                <ApplicationDetailPage />
                              </PermissionGuard>
                            }
                          />
                          {/* Legacy route for backward compatibility - redirects to category view if needed */}
                          <Route
                            path="applications/list"
                            element={
                              <PermissionGuard
                                permissions={[
                                  PERMISSION_KEYS.APPLICATIONS_VIEW_OWN,
                                  PERMISSION_KEYS.APPLICATIONS_VIEW_ALL,
                                ]}
                                showAccessDenied
                              >
                                <ApplicationsListPage />
                              </PermissionGuard>
                            }
                          />
                          <Route
                            path="service"
                            element={
                              <Navigate to="/admin/service/list" replace />
                            }
                          />
                          <Route
                            path="service/list"
                            element={
                              <PermissionGuard
                                permissions={[
                                  PERMISSION_KEYS.SERVICES_VIEW,
                                  PERMISSION_KEYS.SERVICES_CREATE,
                                  PERMISSION_KEYS.SERVICES_EDIT,
                                ]}
                                showAccessDenied
                              >
                                <ServiceCreatorHub />
                              </PermissionGuard>
                            }
                          />
                          <Route
                            path="service/new"
                            element={
                              <PermissionGuard
                                permission={PERMISSION_KEYS.SERVICES_CREATE}
                                showAccessDenied
                              >
                                <ServiceCreatorPage />
                              </PermissionGuard>
                            }
                          />
                          <Route
                            path="service/edit/:serviceId"
                            element={
                              <PermissionGuard
                                permission={PERMISSION_KEYS.SERVICES_EDIT}
                                showAccessDenied
                              >
                                <ServiceCreatorPage />
                              </PermissionGuard>
                            }
                          />
                          <Route
                            path="services-browser"
                            element={
                              <PermissionGuard
                                permission={PERMISSION_KEYS.SERVICES_VIEW}
                                showAccessDenied
                              >
                                <ServicesBrowserPage />
                              </PermissionGuard>
                            }
                          />
                          <Route
                            path="service/categories"
                            element={
                              <PermissionGuard
                                permission={PERMISSION_KEYS.SERVICES_VIEW}
                                showAccessDenied
                              >
                                <ServicesHubPage />
                              </PermissionGuard>
                            }
                          />
                          <Route
                            path="service/categories/create"
                            element={
                              <PermissionGuard
                                permission={PERMISSION_KEYS.SERVICES_CREATE}
                                showAccessDenied
                              >
                                <TemplateEditorPage />
                              </PermissionGuard>
                            }
                          />
                          <Route
                            path="service/categories/edit/:categoryId"
                            element={
                              <PermissionGuard
                                permission={PERMISSION_KEYS.SERVICES_EDIT}
                                showAccessDenied
                              >
                                <TemplateEditorPage />
                              </PermissionGuard>
                            }
                          />
                          <Route
                            path="profile"
                            element={
                              <PermissionGuard
                                permission={PERMISSION_KEYS.PROFILE_VIEW}
                                showAccessDenied
                              >
                                <ProfilePage />
                              </PermissionGuard>
                            }
                          />
                          <Route
                            path="users"
                            element={
                              <PermissionGuard
                                permission={PERMISSION_KEYS.USERS_VIEW}
                                showAccessDenied
                              >
                                <UsersManagementPage />
                              </PermissionGuard>
                            }
                          />
                          {/*
                            Super Admin only, and deliberately not a permission
                            key: Super Admin holds every key by construction, so
                            a key check here would widen to any Admin who was
                            granted it. The Edge Function repeats this check —
                            this guard only keeps the page from being offered.
                          */}
                          <Route
                            path="view-as/:userId"
                            element={
                              <SuperAdminGuard showAccessDenied>
                                <ViewAsPage />
                              </SuperAdminGuard>
                            }
                          />
                          <Route
                            path="roles"
                            element={
                              <PermissionGuard
                                permission={PERMISSION_KEYS.ROLES_VIEW}
                                showAccessDenied
                              >
                                <RolesManagementPage />
                              </PermissionGuard>
                            }
                          />
                          <Route
                            path="reports"
                            element={
                              <PermissionGuard
                                permission={PERMISSION_KEYS.REPORTS_VIEW}
                                showAccessDenied
                              >
                                <ReportsPage />
                              </PermissionGuard>
                            }
                          />
                          <Route
                            path="support-messages"
                            element={
                              <PermissionGuard
                                permission={PERMISSION_KEYS.SUPPORT_VIEW}
                                showAccessDenied
                              >
                                <SupportMessagesPage />
                              </PermissionGuard>
                            }
                          />
                          <Route
                            path="faqs"
                            element={
                              <PermissionGuard
                                permission={PERMISSION_KEYS.FAQS_VIEW}
                                showAccessDenied
                              >
                                <FAQsManagementPage />
                              </PermissionGuard>
                            }
                          />
                          <Route
                            path="announcements"
                            element={
                              <PermissionGuard
                                permission={
                                  PERMISSION_KEYS.ANNOUNCEMENTS_VIEW
                                }
                                showAccessDenied
                              >
                                <AnnouncementsManagementPage />
                              </PermissionGuard>
                            }
                          />
                          <Route
                            path="cape"
                            element={
                              <PermissionGuard
                                permission={PERMISSION_KEYS.CAPE_VIEW}
                                showAccessDenied
                              >
                                <CapeApplicationsPage />
                              </PermissionGuard>
                            }
                          />
                          <Route
                            path="cape/:id"
                            element={
                              <PermissionGuard
                                permission={PERMISSION_KEYS.CAPE_VIEW}
                                showAccessDenied
                              >
                                <CapeApplicationDetailPage />
                              </PermissionGuard>
                            }
                          />
                          {/* Development only (see CleanupPage above). */}
                          {CleanupPage && (
                            <Route
                              path="cleanup"
                              element={
                                <SuperAdminGuard showAccessDenied>
                                  <CleanupPage />
                                </SuperAdminGuard>
                              }
                            />
                          )}
                        </Route>

                        {/* 404 Not Found */}
                        <Route path="*" element={<NotFoundPage />} />
                      </Routes>
                    </Suspense>
                  </Router>
                </TooltipProvider>
              </AppReadyProvider>
            </PermissionsProvider>
          </AuthProvider>
        </QueryProvider>
      </I18nProvider>
    </ThemeProvider>
  );
}

export default App;
